import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../utils/api';
import i18n from '../i18n';

const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT = 3600 * 1000; // 3600 secunde (1 ora) fara activitate -> logout automat
const HEARTBEAT_MIN_INTERVAL = 30 * 1000; // heartbeat/refresh trimis cel mult la 30s pe activitate

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);
  const logoutRef = useRef(null);

  // Functie logout stabila (nu depinde de state)
  const doLogout = useCallback(() => {
    // Notifica backend ca sesiunea se inchide explicit (logout)
    const t = localStorage.getItem('oferte_token') || sessionStorage.getItem('oferte_token');
    if (t) {
      try {
        fetch(`${API_URL}/api/sesiuni/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${t}` },
          keepalive: true,
        }).catch(() => {});
      } catch (e) { /* ignore */ }
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem('oferte_token');
    localStorage.removeItem('oferte_user');
    localStorage.removeItem('oferte_was_guest');
    sessionStorage.removeItem('oferte_token');
    sessionStorage.removeItem('oferte_user');
    sessionStorage.removeItem('oferte_is_guest');
    sessionStorage.removeItem('oferte_session_alive');
  }, []);

  // Pastreaza referinta la logout pentru timer
  logoutRef.current = doLogout;

  // Verificare token la pornirea aplicatiei
  useEffect(() => {
    const verifyToken = async () => {
      // Sesiune 35: guest token in sessionStorage (efemer, dispare la inchidere tab)
      const isGuestSess = sessionStorage.getItem('oferte_is_guest') === 'true';
      // Sesiune 75 (security): daca was_guest e in localStorage dar is_guest NU e in sessionStorage,
      // inseamna ca browserul a fost inchis si redeschis. Nu reactivam sesiunea guest persistata.
      const wasGuest = localStorage.getItem('oferte_was_guest') === 'true';
      if (wasGuest && !isGuestSess) {
        localStorage.removeItem('oferte_token');
        localStorage.removeItem('oferte_user');
        localStorage.removeItem('oferte_was_guest');
        sessionStorage.removeItem('oferte_token');
        sessionStorage.removeItem('oferte_user');
        sessionStorage.removeItem('oferte_is_guest');
        sessionStorage.removeItem('oferte_session_alive');
        setLoading(false);
        return;
      }
      // Sesiune 76 (security): acelasi principiu pentru admin/distribuitor.
      // Daca tokenul exista in localStorage dar markerul oferte_session_alive lipseste
      // din sessionStorage, inseamna ca tabul a fost inchis si redeschis. Purge si forteaza login.
      const sessionAlive = sessionStorage.getItem('oferte_session_alive') === 'true';
      const hasLocalToken = !!localStorage.getItem('oferte_token');
      if (hasLocalToken && !sessionAlive && !isGuestSess) {
        localStorage.removeItem('oferte_token');
        localStorage.removeItem('oferte_user');
        localStorage.removeItem('oferte_was_guest');
        sessionStorage.removeItem('oferte_token');
        sessionStorage.removeItem('oferte_user');
        sessionStorage.removeItem('oferte_is_guest');
        sessionStorage.removeItem('oferte_session_alive');
        setLoading(false);
        return;
      }
      const savedToken = isGuestSess
        ? sessionStorage.getItem('oferte_token')
        : (localStorage.getItem('oferte_token') || sessionStorage.getItem('oferte_token'));
      if (!savedToken) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/auth/verify`, {
          headers: { 'Authorization': `Bearer ${savedToken}` }
        });

        // Batch 1 (stabilitate): pastreaza sesiunea din storage la erori tranzitorii.
        // Backend-ul poate fi temporar suprasolicitat (ex: scanare ScanX mare) si poate
        // raspunde lent, 5xx, 429 sau 403; in aceste cazuri NU scoatem userul din aplicatie.
        const _keepSessionFromCache = () => {
          setToken(savedToken);
          const cachedUserStr = isGuestSess
            ? sessionStorage.getItem('oferte_user')
            : (localStorage.getItem('oferte_user') || sessionStorage.getItem('oferte_user'));
          if (cachedUserStr) {
            try { setUser(JSON.parse(cachedUserStr)); } catch (err) { /* ignore */ }
          }
          if (!isGuestSess) {
            sessionStorage.setItem('oferte_session_alive', 'true');
          }
        };

        if (res.ok) {
          const data = await res.json();
          setToken(savedToken);
          setUser(data.user);
          if (isGuestSess) {
            sessionStorage.setItem('oferte_user', JSON.stringify(data.user));
          } else {
            localStorage.setItem('oferte_user', JSON.stringify(data.user));
            // Sesiune 76 (security): re-marcheaza tabul ca sesiune vie (acoperire refresh F5).
            sessionStorage.setItem('oferte_session_alive', 'true');
          }
          // Sync UI language cu setarea persistata pe user (sesiune 31 iter 3)
          if (data.user?.lang && i18n.resolvedLanguage !== data.user.lang) {
            i18n.changeLanguage(data.user.lang);
            try { localStorage.setItem('oferte_lang', data.user.lang); } catch (e) { /* ignore */ }
          }
        } else if (res.status === 401 || res.status === 403) {
          // 401 = token invalid/expirat/invalidat; 403 = cont dezactivat.
          // Ambele sunt respingeri auth definitive de la /api/auth/verify -> curatam sesiunea.
          localStorage.removeItem('oferte_token');
          localStorage.removeItem('oferte_user');
          localStorage.removeItem('oferte_was_guest');
          sessionStorage.removeItem('oferte_token');
          sessionStorage.removeItem('oferte_user');
          sessionStorage.removeItem('oferte_is_guest');
          sessionStorage.removeItem('oferte_session_alive');
        } else {
          // Eroare tranzitorie (5xx / 429 / backend suprasolicitat): pastram sesiunea.
          _keepSessionFromCache();
        }
      } catch (e) {
        // Eroare de retea / timeout (backend suprasolicitat): NU scoatem userul.
        setToken(savedToken);
        const cachedUserStr = isGuestSess
          ? sessionStorage.getItem('oferte_user')
          : (localStorage.getItem('oferte_user') || sessionStorage.getItem('oferte_user'));
        if (cachedUserStr) {
          try { setUser(JSON.parse(cachedUserStr)); } catch (err) { /* ignore */ }
        }
        if (!isGuestSess) {
          sessionStorage.setItem('oferte_session_alive', 'true');
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, []);

  // Timer inactivitate - 10 minute
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    inactivityTimer.current = setTimeout(() => {
      if (logoutRef.current) {
        logoutRef.current();
      }
      window.location.href = '/login';
    }, INACTIVITY_TIMEOUT);
  }, []);

  useEffect(() => {
    if (!token) {
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
      return;
    }

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler));
    resetInactivityTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, [token, resetInactivityTimer]);

  const login = (tokenData, userData) => {
    setToken(tokenData);
    setUser(userData);
    localStorage.setItem('oferte_token', tokenData);
    localStorage.setItem('oferte_user', JSON.stringify(userData));
    localStorage.removeItem('oferte_was_guest');
    sessionStorage.removeItem('oferte_is_guest');
    // Sesiune 76 (security): marker sessionStorage care dispare la inchidere tab.
    // La urmatoarea deschidere, verifyToken detecteaza absenta markerului si forteaza login.
    sessionStorage.setItem('oferte_session_alive', 'true');
    // Sync UI language cu user.lang la login (sesiune 31 iter 3)
    if (userData?.lang && i18n.resolvedLanguage !== userData.lang) {
      i18n.changeLanguage(userData.lang);
      try { localStorage.setItem('oferte_lang', userData.lang); } catch (e) { /* ignore */ }
    }
  };

  const loginGuest = (tokenData, userData) => {
    // Sesiune 35: token guest stocat in sessionStorage (dispare la inchidere tab)
    // Sesiune 39: salvam in localStorage suplimentar pentru compatibilitate cu codul
    // existent care citeste DOAR din localStorage (LanguageSelector, ScanX, etc).
    // Sesiune 75 (security): adaugam marker "was_guest" in localStorage. La verifyToken,
    // daca was_guest && !is_guest (browser inchis-redeschis), purge tot si redirect login.
    setToken(tokenData);
    setUser(userData);
    sessionStorage.setItem('oferte_token', tokenData);
    sessionStorage.setItem('oferte_user', JSON.stringify(userData));
    sessionStorage.setItem('oferte_is_guest', 'true');
    localStorage.setItem('oferte_token', tokenData);
    localStorage.setItem('oferte_user', JSON.stringify(userData));
    localStorage.setItem('oferte_was_guest', 'true');
  };

  // Heartbeat tracking sesiuni: trimis pe activitate user (click/touch/key), debounced la 30s.
  // Fara activitate timp de INACTIVITY_TIMEOUT, sesiunea expira (vezi useEffect-ul de inactivitate
  // care cheama doLogout dupa 100s). pagehide trimite logout instant pe inchidere tab.
  useEffect(() => {
    if (!token) return;
    let lastBeatAt = 0;
    // Sesiune glisanta: reinnoieste tokenul din storage cat timp userul e activ, ca sesiunea
    // sa nu expire la 8 ore pentru cine lucreaza continuu. Tokenul reinnoit pastreaza iat-ul
    // original pe backend, deci logout-all al adminului ramane functional.
    const doRefresh = () => {
      const isGuest = sessionStorage.getItem('oferte_is_guest') === 'true';
      const t = isGuest
        ? sessionStorage.getItem('oferte_token')
        : (localStorage.getItem('oferte_token') || sessionStorage.getItem('oferte_token'));
      if (!t) return;
      fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${t}` }
      })
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          if (data && data.token) {
            if (isGuest) {
              sessionStorage.setItem('oferte_token', data.token);
            } else {
              localStorage.setItem('oferte_token', data.token);
            }
          }
        })
        .catch(() => {});
    };
    const sendHeartbeat = () => {
      const now = Date.now();
      if (now - lastBeatAt < HEARTBEAT_MIN_INTERVAL) return;
      lastBeatAt = now;
      fetch(`${API_URL}/api/sesiuni/heartbeat`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
      doRefresh();
    };
    // Primul heartbeat la mount ca sa marcheze sesiunea activa pe backend.
    lastBeatAt = Date.now();
    fetch(`${API_URL}/api/sesiuni/heartbeat`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    }).catch(() => {});
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, sendHeartbeat));

    // Inchide sesiunea cand utilizatorul inchide tabul/navigheaza in afara aplicatiei.
    // sendBeacon NU permite header-e custom, asa ca trimitem user_id in body si
    // backend-ul valideaza prin token din header daca e prezent (pe pagehide noi
    // folosim fetch keepalive cu header).
    const handleUnload = () => {
      try {
        const userStr = localStorage.getItem('oferte_user');
        const uid = userStr ? (JSON.parse(userStr).id || '') : '';
        const blob = new Blob([JSON.stringify({ user_id: uid })], { type: 'application/json' });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(`${API_URL}/api/sesiuni/logout`, blob);
        } else {
          fetch(`${API_URL}/api/sesiuni/logout`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            keepalive: true,
          }).catch(() => {});
        }
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('pagehide', handleUnload);

    return () => {
      events.forEach(e => window.removeEventListener(e, sendHeartbeat));
      window.removeEventListener('pagehide', handleUnload);
    };
  }, [token]);

  const logout = () => {
    doLogout();
  };

  const isAdmin = user?.rol === 'admin';
  const canSeePreturi = isAdmin || (user?.grup_nume || '').toLowerCase() === 'contab';

  const hasPermission = (modul, actiune = 'vizualizare', legacyModul = null) => {
    if (isAdmin) return true;
    const permisiuni = user?.permisiuni || {};
    // Verificare directa pe ecran - daca exista explicit, respecta-l
    if (permisiuni[modul] !== undefined) {
      return permisiuni[modul]?.[actiune] === true;
    }
    // Sesiune 80: fallback legacyModul + LEGACY_MAP ELIMINATE - cauzau leak
    // de permisiuni cand un grup avea cheia parentala (scan-x, configurator)
    // si toate sub-paginile cu modul=acelasi parinte deveneau vizibile.
    // Permisiunile sunt strict granulare per ecranId din colectia drepturi.
    return false;
  };

  const canView = (modul, legacyModul = null) => hasPermission(modul, 'vizualizare', legacyModul);
  const canCreate = (modul, legacyModul = null) => hasPermission(modul, 'creare', legacyModul);
  const canEdit = (modul, legacyModul = null) => hasPermission(modul, 'editare', legacyModul);
  const canDelete = (modul, legacyModul = null) => hasPermission(modul, 'stergere', legacyModul);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      loginGuest,
      logout,
      loading,
      isAdmin,
      canSeePreturi,
      hasPermission,
      canView,
      canCreate,
      canEdit,
      canDelete
    }}>
      {children}
    </AuthContext.Provider>
  );
};
